// cspell:ignore Sommel
// src/services/masterEnhancementService.js
/**
 * SommelAI Master Enhancement Service
 *
 * Orchestrates both AI-powered enhancement and external API data aggregation
 * to create comprehensive beverage intelligence profiles that combine:
 * - AI-generated insights and analysis
 * - Professional ratings from external sources
 * - Market data and pricing information
 * - User reviews and community ratings
 * - Enhanced metadata and enriched information
 */

import { enhanceBeverageData } from "./aiEnhancementPipeline.js";
import { aggregateExternalData } from "./externalApiIntegration.js";
import { callAnthropicAI, AI_MODELS } from "./aiService.js";
import {
  generateFallbackEnhancement,
  enhanceWithFallbackData,
} from "./fallbackEnhancementService.js";
import analyticsService from "./enhancementAnalyticsService.js";

/**
 * Master enhancement function that combines AI and external data
 * @param {Object} beverage - Basic beverage information
 * @param {string} category - Beverage category
 * @param {Object} options - Enhancement options
 * @returns {Promise<Object>} Fully enhanced beverage data
 */
export async function enhanceBeverageComprehensively(
  beverage,
  category,
  options = {}
) {
  // Start analytics tracking
  const sessionId = analyticsService.trackEnhancementStart(beverage, category);
  const startTime = Date.now();

  try {
    console.log(
      `🚀 Starting comprehensive enhancement for ${beverage.name} (${category})`
    );

    const enhancementResult = {
      ...beverage,
      masterEnhancement: {
        sessionId,
        timestamp: new Date().toISOString(),
        version: "1.0",
        processingTime: 0,
        dataSources: {
          ai: { status: "pending", sources: [] },
          external: { status: "pending", sources: [] },
          synthesis: { status: "pending" },
        },
        confidence: {
          overall: 0,
          ai: 0,
          external: 0,
          synthesis: 0,
        },
        status: "processing",
      },
    };

    // Step 1: Run AI enhancement and external API aggregation in parallel
    console.log(
      "📊 Running AI enhancement and external API aggregation in parallel..."
    );

    const [aiResult, externalResult] = await Promise.allSettled([
      enhanceBeverageData(beverage, category, {
        ...options,
        includeInternalRatings: false,
      }),
      aggregateExternalData(beverage, category, options),
    ]);

    // Step 2: Process AI enhancement results
    let aiEnhancedData = {};
    if (aiResult.status === "fulfilled") {
      aiEnhancedData = aiResult.value;
      enhancementResult.masterEnhancement.dataSources.ai = {
        status: "success",
        sources: aiEnhancedData.enhanced?.aiSources || [],
        confidence: aiEnhancedData.enhanced?.confidenceScore || 0,
      };
      enhancementResult.masterEnhancement.confidence.ai =
        aiEnhancedData.enhanced?.confidenceScore || 0;
      analyticsService.trackSourceComplete(sessionId, "ai", true, {
        confidence: aiEnhancedData.enhanced?.confidenceScore || 0,
        sources: aiEnhancedData.enhanced?.aiSources || [],
      });
      console.log("✅ AI enhancement completed successfully");
    } else {
      console.warn("⚠️ AI enhancement failed:", aiResult.reason);
      enhancementResult.masterEnhancement.dataSources.ai = {
        status: "failed",
        error: aiResult.reason?.message || "AI enhancement failed",
        sources: [],
      };
      analyticsService.trackSourceComplete(sessionId, "ai", false);
    }

    // Step 3: Process external API results
    let externalEnhancedData = {};
    if (externalResult.status === "fulfilled") {
      externalEnhancedData = externalResult.value;
      enhancementResult.masterEnhancement.dataSources.external = {
        status: externalEnhancedData.externalData?.status || "success",
        sources: externalEnhancedData.externalData?.sources || [],
        confidence: externalEnhancedData.externalData?.confidence || 0,
      };
      enhancementResult.masterEnhancement.confidence.external =
        externalEnhancedData.externalData?.confidence || 0;
      analyticsService.trackSourceComplete(sessionId, "external", true, {
        confidence: externalEnhancedData.externalData?.confidence || 0,
        sources: externalEnhancedData.externalData?.sources || [],
      });
      console.log("✅ External API aggregation completed successfully");
    } else {
      console.warn(
        "⚠️ External API aggregation failed:",
        externalResult.reason
      );
      enhancementResult.masterEnhancement.dataSources.external = {
        status: "failed",
        error:
          externalResult.reason?.message || "External API aggregation failed",
        sources: [],
      };
      analyticsService.trackSourceComplete(sessionId, "external", false);
    }

    // Step 3.5: Apply fallback enhancement if needed
    if (
      aiResult.status === "rejected" &&
      externalResult.status === "rejected"
    ) {
      console.log(
        "🔄 Both AI and external APIs failed, generating fallback enhancement..."
      );
      const fallbackData = await generateFallbackEnhancement(
        beverage,
        category
      );
      externalEnhancedData = {
        externalData: {
          status: "fallback",
          sources: ["fallback_knowledge_base"],
          confidence: fallbackData.confidence,
          data: fallbackData.data,
        },
      };
      enhancementResult.masterEnhancement.dataSources.external = {
        status: "fallback",
        sources: ["fallback_knowledge_base"],
        confidence: fallbackData.confidence,
      };
      enhancementResult.masterEnhancement.confidence.external =
        fallbackData.confidence;
      analyticsService.trackSourceComplete(sessionId, "fallback", true, {
        confidence: fallbackData.confidence,
        sources: ["fallback_knowledge_base"],
      });
      console.log("✅ Fallback enhancement applied successfully");
    } else if (
      aiResult.status === "rejected" ||
      externalResult.status === "rejected"
    ) {
      // Enhance partial data with fallback information
      console.log("🔄 Enhancing partial data with fallback information...");
      if (externalResult.status === "fulfilled") {
        externalEnhancedData = await enhanceWithFallbackData(
          externalEnhancedData.externalData,
          beverage,
          category
        );
      }
    }

    // Step 4: Synthesize and merge data intelligently
    console.log("🔄 Synthesizing AI and external data...");
    const synthesizedData = await synthesizeEnhancementData(
      aiEnhancedData,
      externalEnhancedData,
      beverage,
      category,
      options
    );

    // Step 5: Finalize enhancement result
    const finalResult = {
      ...beverage,
      ...synthesizedData,
      masterEnhancement: {
        ...enhancementResult.masterEnhancement,
        processingTime: Date.now() - startTime,
        confidence: {
          ...enhancementResult.masterEnhancement.confidence,
          synthesis: synthesizedData.synthesisConfidence || 0,
          overall: calculateOverallConfidence(
            enhancementResult.masterEnhancement.confidence
          ),
        },
        status: "completed",
      },
    };

    console.log(
      `🎉 Comprehensive enhancement completed in ${finalResult.masterEnhancement.processingTime}ms`
    );
    console.log(
      `📈 Overall confidence: ${(
        finalResult.masterEnhancement.confidence.overall * 100
      ).toFixed(1)}%`
    );

    // Track successful completion
    analyticsService.trackEnhancementComplete(sessionId, finalResult);

    return finalResult;
  } catch (error) {
    console.error("❌ Comprehensive enhancement failed:", error);

    // Track failure
    if (sessionId) {
      analyticsService.trackEnhancementFailure(sessionId, error);
    }

    // Return fallback enhancement result instead of throwing
    const currentTime = Date.now();
    return {
      ...beverage,
      masterEnhancement: {
        timestamp: new Date().toISOString(),
        version: "1.0",
        processingTime: currentTime - (startTime || currentTime),
        dataSources: {
          ai: { status: "failed", sources: [] },
          external: { status: "failed", sources: [] },
          synthesis: { status: "failed" },
        },
        confidence: { overall: 0, ai: 0, external: 0, synthesis: 0 },
        status: "failed",
        error: error.message,
      },
    };
  }
}

/**
 * Synthesizes AI-generated and external API data into a unified enhancement
 * @param {Object} aiData - AI enhancement results
 * @param {Object} externalData - External API aggregation results
 * @param {Object} originalBeverage - Original beverage data
 * @param {string} category - Beverage category
 * @param {Object} options - Synthesis options
 * @returns {Promise<Object>} Synthesized enhancement data
 */
async function synthesizeEnhancementData(
  aiData,
  externalData,
  originalBeverage,
  category,
  options = {}
) {
  try {
    console.log("🧠 Running AI synthesis of enhancement data...");

    // Base synthesis structure
    const synthesis = {
      // Merge enhanced metadata
      enhanced: mergeEnhancedMetadata(
        aiData.enhanced,
        externalData.externalData
      ),

      // Synthesize ratings from both sources
      ratings: await synthesizeRatings(
        aiData.ratings,
        externalData.externalData?.aggregatedRatings
      ),

      // Keep AI-generated tasting profile as primary, enrich with external data
      tastingProfile: aiData.tastingProfile || {},

      // Merge producer information
      producer: mergeProducerInfo(
        aiData.producer,
        externalData.externalData?.enrichedInfo
      ),

      // Keep AI food pairings, add market context
      pairings: aiData.pairings || {},

      // Add external market data and pricing
      marketData: externalData.externalData?.marketData || {},

      // Merge additional enriched information
      enrichedInfo: {
        ...(aiData.enrichedInfo || {}),
        ...(externalData.externalData?.enrichedInfo || {}),
      },

      // Add external URLs and references
      externalReferences: extractExternalReferences(externalData.externalData),

      synthesisConfidence: 0,
    };

    // Use AI to create intelligent synthesis commentary
    const synthesisPrompt = createSynthesisPrompt(
      aiData,
      externalData,
      originalBeverage,
      category
    );
    const aiSynthesis = await callAnthropicAI(
      synthesisPrompt,
      AI_MODELS.CLAUDE_3_HAIKU
    );

    if (aiSynthesis.success) {
      try {
        const parsedSynthesis = JSON.parse(aiSynthesis.data);
        synthesis.synthesisInsights = {
          dataQuality: parsedSynthesis.dataQuality || {},
          marketPosition: parsedSynthesis.marketPosition || {},
          recommendation: parsedSynthesis.recommendation || "",
          confidence: parsedSynthesis.confidence || 0.7,
          consensusAnalysis: parsedSynthesis.consensusAnalysis || "",
        };
        synthesis.synthesisConfidence = parsedSynthesis.confidence || 0.7;
      } catch (parseError) {
        console.warn(
          "Failed to parse AI synthesis response, using raw data",
          parseError
        );
        synthesis.synthesisInsights = {
          rawResponse: aiSynthesis.data,
          confidence: 0.5,
        };
        synthesis.synthesisConfidence = 0.5;
      }
    }

    return synthesis;
  } catch (error) {
    console.error("Data synthesis error:", error);
    return {
      enhanced: aiData.enhanced || {},
      ratings: aiData.ratings || {},
      tastingProfile: aiData.tastingProfile || {},
      producer: aiData.producer || {},
      pairings: aiData.pairings || {},
      marketData: externalData.externalData?.marketData || {},
      synthesisConfidence: 0.3,
      synthesisError: error.message,
    };
  }
}

/**
 * Merges enhanced metadata from AI and external sources
 */
function mergeEnhancedMetadata(aiEnhanced, externalData) {
  return {
    timestamp: new Date().toISOString(),
    version: "1.0",
    aiSources: aiEnhanced?.aiSources || [],
    externalSources: externalData?.sources || [],
    confidenceScore: Math.max(
      aiEnhanced?.confidenceScore || 0,
      externalData?.confidence || 0
    ),
    status: "enhanced",
  };
}

/**
 * Synthesizes ratings from AI predictions and external sources
 */
async function synthesizeRatings(aiRatings, externalRatings) {
  const synthesized = {
    sources: [],
    averageScore: 0,
    totalReviews: 0,
    consensusRating: "",
    professional: [],
    user: [],
    aiPredicted: [],
  };

  // Add AI-predicted ratings
  if (aiRatings?.sources) {
    synthesized.aiPredicted = aiRatings.sources.map((rating) => ({
      ...rating,
      type: "ai-predicted",
    }));
  }

  // Add external professional ratings
  if (externalRatings?.professional) {
    synthesized.professional = externalRatings.professional;
    synthesized.sources.push(
      ...externalRatings.professional.map((r) => r.source)
    );
  }

  // Add external user ratings
  if (externalRatings?.user) {
    synthesized.user = externalRatings.user;
    synthesized.sources.push(...externalRatings.user.map((r) => r.source));
  }

  // Calculate weighted average
  const allRatings = [
    ...synthesized.professional,
    ...synthesized.user,
    ...synthesized.aiPredicted,
  ];

  if (allRatings.length > 0) {
    const weightedScores = allRatings.map((rating) => {
      const normalizedScore =
        rating.scale === 100 ? rating.score / 20 : rating.score; // Convert to 5-point scale
      const weight = getWeightForRatingType(rating.type);
      return normalizedScore * weight;
    });

    const totalWeight = allRatings.reduce((sum, rating) => {
      return sum + getWeightForRatingType(rating.type);
    }, 0);

    synthesized.averageScore =
      weightedScores.reduce((sum, score) => sum + score, 0) / totalWeight;
    synthesized.totalReviews =
      synthesized.professional.reduce(
        (sum, r) => sum + (r.reviewCount || 0),
        0
      ) + synthesized.user.reduce((sum, r) => sum + (r.reviewCount || 0), 0);

    // Generate consensus rating
    if (synthesized.averageScore >= 4.5)
      synthesized.consensusRating = "Outstanding";
    else if (synthesized.averageScore >= 4.0)
      synthesized.consensusRating = "Excellent";
    else if (synthesized.averageScore >= 3.5)
      synthesized.consensusRating = "Very Good";
    else if (synthesized.averageScore >= 3.0)
      synthesized.consensusRating = "Good";
    else if (synthesized.averageScore >= 2.5)
      synthesized.consensusRating = "Fair";
    else synthesized.consensusRating = "Below Average";
  }

  return synthesized;
}

/**
 * Merges producer information from AI and external sources
 */
function mergeProducerInfo(aiProducer, externalEnriched) {
  const merged = { ...(aiProducer || {}) };

  // Enhance with external data where available
  if (externalEnriched) {
    Object.keys(externalEnriched).forEach((source) => {
      const data = externalEnriched[source];
      if (data.winery && !merged.name) merged.name = data.winery;
      if (data.appellation && !merged.location?.appellation) {
        merged.location = { ...merged.location, appellation: data.appellation };
      }
      if (data.vintage && !merged.vintage) merged.vintage = data.vintage;
    });
  }

  return merged;
}

/**
 * Extracts external references and URLs for further exploration
 */
function extractExternalReferences(externalData) {
  const references = [];

  if (externalData?.enrichedInfo) {
    Object.keys(externalData.enrichedInfo).forEach((source) => {
      const data = externalData.enrichedInfo[source];
      if (data.url) {
        references.push({
          source,
          url: data.url,
          type: "product_page",
          description: `View on ${source}`,
        });
      }
    });
  }

  if (externalData?.marketData?.pricing) {
    externalData.marketData.pricing.forEach((pricing) => {
      if (pricing.url) {
        references.push({
          source: pricing.source,
          url: pricing.url,
          type: "purchase",
          description: `Purchase from ${pricing.source}`,
          price: pricing.price,
          currency: pricing.currency,
        });
      }
    });
  }

  return references;
}

/**
 * Creates synthesis prompt for AI analysis
 */
function createSynthesisPrompt(aiData, externalData, beverage, category) {
  return `You are analyzing beverage data from multiple sources for a ${category} called "${
    beverage.name
  }".

AI-Generated Data:
${JSON.stringify(aiData, null, 2)}

External API Data:
${JSON.stringify(externalData?.externalData, null, 2)}

Please provide a synthesis analysis in JSON format with the following structure:
{
  "dataQuality": {
    "aiDataScore": 0.0-1.0,
    "externalDataScore": 0.0-1.0,
    "consistencyScore": 0.0-1.0,
    "completenessScore": 0.0-1.0
  },
  "marketPosition": {
    "priceRange": "budget|mid-range|premium|luxury",
    "availability": "widely available|limited|rare",
    "popularity": "mainstream|niche|cult following",
    "trendingStatus": "rising|stable|declining"
  },
  "consensusAnalysis": "Brief analysis of consensus between AI and external sources",
  "recommendation": "Overall recommendation based on combined data",
  "confidence": 0.0-1.0
}

Focus on identifying patterns, inconsistencies, and opportunities to enhance the user experience.`;
}

/**
 * Helper function to get weight for rating type
 */
function getWeightForRatingType(ratingType) {
  switch (ratingType) {
    case "professional":
      return 1.5;
    case "user":
      return 1.0;
    default:
      return 0.8; // AI predictions weighted lower
  }
}

/**
 * Calculates overall confidence score from individual confidence scores
 */
function calculateOverallConfidence(confidenceScores) {
  const scores = [
    confidenceScores.ai || 0,
    confidenceScores.external || 0,
    confidenceScores.synthesis || 0,
  ];

  // Weighted average: AI and external data are primary, synthesis is confirmation
  const weights = [0.4, 0.4, 0.2];
  const weightedSum = scores.reduce(
    (sum, score, index) => sum + score * weights[index],
    0
  );

  return Math.min(1.0, Math.max(0.0, weightedSum));
}

/**
 * Batch enhancement function for multiple beverages
 * @param {Array} beverages - Array of beverage objects with category
 * @param {Object} options - Batch processing options
 * @returns {Promise<Array>} Array of enhanced beverages
 */
export async function enhanceBeveragesBatch(beverages, options = {}) {
  const batchSize = options.batchSize || 3;
  const concurrency = options.concurrency || 2;
  const results = [];

  console.log(
    `🚀 Starting batch enhancement for ${beverages.length} beverages`
  );
  console.log(`📦 Batch size: ${batchSize}, Concurrency: ${concurrency}`);

  for (let i = 0; i < beverages.length; i += batchSize) {
    const batch = beverages.slice(i, i + batchSize);
    const batchPromises = batch.map(async (item, index) => {
      try {
        const enhanced = await enhanceBeverageComprehensively(
          item.beverage,
          item.category,
          { ...options, batchIndex: i + index }
        );
        return { success: true, data: enhanced, originalIndex: i + index };
      } catch (error) {
        console.error(`Batch enhancement failed for item ${i + index}:`, error);
        return {
          success: false,
          error: error.message,
          data: item.beverage,
          originalIndex: i + index,
        };
      }
    });

    const batchResults = await Promise.allSettled(batchPromises);
    results.push(
      ...batchResults.map((result) =>
        result.status === "fulfilled"
          ? result.value
          : {
              success: false,
              error: "Promise rejected",
              data: null,
            }
      )
    );

    // Rate limiting between batches
    if (i + batchSize < beverages.length) {
      const delay = options.batchDelay || 1000;
      console.log(`⏸️ Waiting ${delay}ms before next batch...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  const successCount = results.filter((r) => r.success).length;
  console.log(
    `✅ Batch enhancement completed: ${successCount}/${beverages.length} successful`
  );

  return results;
}

/**
 * Get enhancement analytics dashboard
 */
export function getEnhancementAnalytics() {
  return analyticsService.getAnalyticsDashboard();
}

/**
 * Get performance recommendations
 */
export function getPerformanceRecommendations() {
  return analyticsService.getPerformanceRecommendations();
}

/**
 * Export analytics data
 */
export function exportAnalyticsData(format = "json") {
  return analyticsService.exportAnalyticsData(format);
}

/**
 * Get session details
 */
export function getSessionAnalytics(sessionId) {
  return analyticsService.getSessionAnalytics(sessionId);
}

/**
 * Clear analytics (for testing)
 */
export function clearAnalytics() {
  return analyticsService.clearAnalytics();
}

const masterEnhancementService = {
  enhanceBeverageComprehensively,
  enhanceBeveragesBatch,
  calculateOverallConfidence,
  getEnhancementAnalytics,
  getPerformanceRecommendations,
  exportAnalyticsData,
  getSessionAnalytics,
  clearAnalytics,
};

export default masterEnhancementService;
